<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import EditDialog from '@/components/dialogs/EditDialog.vue'
import { UpdateUpload, Upload } from '@/generated/graphql'
import useVuelidate from '@vuelidate/core'
import { reactive, watch } from 'vue'
import { fileName } from '@/validation'
import TextField from '@/components/input/TextField.vue'

const props = defineProps<{
  modelValue?: Upload
}>()

const emit = defineEmits<{
  'update:modelValue': [value: Upload | undefined]
  save: [value: UpdateUpload]
}>()

type Model = Partial<UpdateUpload>
const modelKeys: Array<keyof UpdateUpload> = ['id', 'name']

const { t } = useI18n()

const model = reactive<Model>({})
const validation = useVuelidate<Model>(
  {
    name: { fileName },
  },
  model,
)

watch(
  () => props.modelValue,
  (v) => {
    if (!v) {
      return
    }

    modelKeys.forEach((key) => {
      if (key in v) {
        model[key] = v[key] as Model[typeof key]
      }
    })
    validation.value.$reset()
  },
  { immediate: true },
)

function close() {
  emit('update:modelValue', undefined)
}

function save() {
  validation.value.$touch()
  if (validation.value.$invalid) {
    return
  }

  emit('save', model as UpdateUpload)
  emit('update:modelValue', undefined)
}
</script>

<template>
  <edit-dialog
    :model-value="!!props.modelValue"
    :validation="validation"
    :entity="t('entity.upload.singular')"
    @update:model-value="
      (v) => {
        if (!v) emit('update:modelValue', undefined)
      }
    "
    @save="save"
    @cancel="close"
  >
    <text-field
      v-model="model.name"
      :label="t('entity.upload.field.name')"
      :validation="validation.name"
    />
  </edit-dialog>
</template>

<style scoped lang="scss"></style>
